/**
 * FAC3LESS Platinum direct purchase — the app's single reading of the referral
 * and hand-over contract (docs/fac3less/PLATINUM_DIREKTKAUF_PLAN.md, section B;
 * customer decisions 2026-10-09). Mirrors apps/globalhealth/src/services/funnel-intent.ts.
 *
 * The static landing page (`/`) and every referral link send visitors to
 * `/r[/:sponsorId][?ref=<id>&plan=platinum]`. The partner referral is kept in
 * `localStorage['fac3less.referral']` ({ id, at }, 90 days) — last touch: the
 * newest explicit link (path or ?ref) wins, a visit without one keeps the stored
 * value. The sponsor is locked only by the backend (SponsorAttribution) at the
 * first confirmed full payment — never by visiting or creating the account.
 */

/** The only product sold through the direct checkout (customer decision 2). */
export const PLATINUM_PLAN_SLUG = 'platinum';
export const PLATINUM_PRODUCT_CODE = 'CARD_PLATINUM';

/** Where the referral is kept across visits. Same key/format the static landing writes. */
export const SITE_REFERRAL_KEY = 'fac3less.referral';

/** Per-tab copy of the sponsor (pre-existing key of the Card Finder funnel, still honoured). */
export const SESSION_SPONSOR_KEY = 'fac3less.funnel.sponsor_id';

/** Lifetime of the stored referral: 90 days. */
export const SITE_REFERRAL_TTL_DAYS = 90;
const SITE_REFERRAL_TTL_MS = SITE_REFERRAL_TTL_DAYS * 24 * 60 * 60 * 1000;

/** Which sponsor source wins when several are present, first match wins: the
 *  path of a partner link (/r/123), then ?ref=123, then what this tab
 *  remembered, then the stored referral. */
export const SPONSOR_SOURCE_PRECEDENCE = ['path', 'query', 'session', 'site'] as const;
export type SponsorSource = typeof SPONSOR_SOURCE_PRECEDENCE[number];

export function resolveSponsor(candidates: Partial<Record<SponsorSource, string | null>>): string | null {
  for (const source of SPONSOR_SOURCE_PRECEDENCE) {
    const id = candidates[source];
    if (id) return id;
  }
  return null;
}

/** A sponsor id as the funnel accepts it: digits, not "0". */
export function validSponsorId(value: string | null | undefined): string | null {
  const id = (value ?? '').trim();
  return /^\d+$/.test(id) && id !== '0' ? id : null;
}

/** The stored referral, or null when absent, malformed, "0" or older than 90 days. */
export function readSiteReferral(now = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(SITE_REFERRAL_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as { id?: string | number; at?: number };
    const id = validSponsorId(String(entry?.id ?? ''));
    if (!id) return null;
    if (typeof entry.at !== 'number' || now - entry.at > SITE_REFERRAL_TTL_MS) return null;
    return id;
  } catch {
    return null;
  }
}

/**
 * Last touch: an explicit referral link (path or ?ref) replaces whatever this tab
 * and the browser remembered, with a fresh 90-day lifetime.
 */
export function rememberReferral(id: string | null | undefined, now = Date.now()): void {
  const sponsor = validSponsorId(id);
  if (!sponsor) return;
  try { sessionStorage.setItem(SESSION_SPONSOR_KEY, sponsor); } catch { /* storage blocked */ }
  try { localStorage.setItem(SITE_REFERRAL_KEY, JSON.stringify({ id: sponsor, at: now })); } catch { /* storage blocked */ }
}

/**
 * The newest referral this browser holds: the given explicit value (query), then
 * this tab's copy, then the stored referral. Sent as `sponsor_id` with every
 * purchase call — the backend decides whether it still applies.
 */
export function currentReferral(explicit?: string | null): string | null {
  let fromSession: string | null = null;
  try { fromSession = validSponsorId(sessionStorage.getItem(SESSION_SPONSOR_KEY)); } catch { /* storage blocked */ }
  return resolveSponsor({ query: validSponsorId(explicit), session: fromSession, site: readSiteReferral() });
}

/** Captures `?ref=<id>` from the URL the app was opened with (any route). */
export function captureReferralFromLocation(search: string = window.location.search): void {
  try {
    const ref = new URLSearchParams(search).get('ref');
    if (ref) rememberReferral(ref);
  } catch { /* malformed query — ignore */ }
}

// ----- "Sign in and come back to this purchase" -------------------------------
// The login page sends a signed-in user to RouteService's `homeUrl`. To return a
// visitor with an existing account to the purchase they started, the purchase URL
// is put there for one sign-in; the previous home is restored on the next start
// of the direct checkout or the dashboard (consumeLoginReturn).

const LOGIN_RETURN_KEY = 'fac3less.checkout.login_return';

export function returnHereAfterLogin(target: string): void {
  try {
    const previous = localStorage.getItem('homeUrl');
    sessionStorage.setItem(LOGIN_RETURN_KEY, JSON.stringify({ previous }));
    localStorage.setItem('homeUrl', target);
  } catch { /* storage blocked — the login page then lands on the dashboard */ }
}

export function consumeLoginReturn(): void {
  try {
    const raw = sessionStorage.getItem(LOGIN_RETURN_KEY);
    if (!raw) return;
    sessionStorage.removeItem(LOGIN_RETURN_KEY);
    const previous = (JSON.parse(raw) ?? {}).previous as string | null;
    if (previous) localStorage.setItem('homeUrl', previous);
    else localStorage.removeItem('homeUrl');
  } catch { /* ignore */ }
}
